/**
 * Read solo-game history records for a given user.
 *
 * Backed by Firestore once game persistence (task #62) is wired. For now
 * this service is the seam: callers depend on it, the impl evolves.
 */

import { db } from './firebase';
import { collection, query, where, orderBy, limit as fsLimit, getDocs } from 'firebase/firestore';

export interface SoloGameRecord {
  gameId: string;
  /** When the game completed (ms since epoch). */
  endedAt: number;
  /** Difficulty Atom played at (easy / medium / hard / expert / insane). */
  difficulty: string;
  /** Total rounds played. */
  rounds: number;
  /** Final score for the player who is the subject of this query. */
  youScore: number;
  /** Final score for Atom in that game. */
  botScore: number;
  /** From the player's perspective. */
  outcome: 'win' | 'loss' | 'draw';
}

/**
 * Pull the latest N solo games for `userId`, newest first. Returns [] if
 * the user has no history, the collection is empty, or Firestore errors —
 * this is a soft-fail surface (used to enrich the Solo Setup screen) and
 * should never block startup.
 */
export async function fetchSoloHistory(userId: string, limit = 5): Promise<SoloGameRecord[]> {
  if (!db || !userId) return [];
  try {
    const q = query(
      collection(db, 'gameRecords'),
      where('participantIds', 'array-contains', userId),
      where('mode', '==', 'solo'),
      orderBy('endedAt', 'desc'),
      fsLimit(limit),
    );
    const snap = await getDocs(q);
    const out: SoloGameRecord[] = [];
    snap.forEach(doc => {
      const d = doc.data() as any;
      const youScore = d.scores?.[userId] ?? 0;
      // The bot's id in solo games is the player whose `isBot === true`.
      const botEntry = (d.players || []).find((p: any) => p.isBot);
      const botScore = botEntry ? d.scores?.[botEntry.id] ?? 0 : 0;
      const outcome: SoloGameRecord['outcome'] =
        youScore > botScore ? 'win' :
        youScore < botScore ? 'loss' : 'draw';
      out.push({
        gameId: doc.id,
        endedAt: d.endedAt?.toMillis ? d.endedAt.toMillis() : (d.endedAt || Date.now()),
        difficulty: d.difficulty || 'medium',
        rounds: d.rounds || (d.roundDetails?.length ?? 0),
        youScore,
        botScore,
        outcome,
      });
    });
    return out;
  } catch (e) {
    // Missing collection / no permission / offline — quietly empty.
    console.warn('[soloHistoryService] fetchSoloHistory failed:', (e as any)?.message || e);
    return [];
  }
}
